import { SKY_WORLD } from './86-sky-world-scale.mjs';
import {
  SKY_VESPER,
  createSkyVesperCrystal,
  stepSkyVesperPulse,
  stepSkyVesperRing,
  skyVesperQuietTarget,
} from './125-sky-vesper.mjs';

export const SKY_VESPER_TEACHING = Object.freeze({
  status: 'implemented',
  summary: 'Quiet gap → real crystal return; optional practice, native review open',
});
const pilot = (s) => ({ x: s.x, y: s.y, z: s.pilotZ ?? SKY_WORLD.pilotZ });
function create(state) {
  const rig = state.authoredPropRigs?.['vesper-crystal'];
  if (!rig) return null;
  const emitter = createSkyVesperCrystal(state, rig, 0, [0, 24, -60]);
  const reflectors = [-1, 1].map((side) =>
    createSkyVesperCrystal(state, rig, side, [side * 6, 12, -24]),
  );
  if (!emitter || reflectors.some((prop) => !prop)) return null;
  return {
    sector: state.sector,
    age: 0,
    phase: 'quiet',
    props: [emitter, ...reflectors],
    emitter: { ...emitter.weakpoint },
    rings: [],
    pulses: [],
    pilot: pilot(state),
    quietTarget: null,
    baitPoints: [],
    locked: null,
    emittedRing: false,
    emittedPulse: false,
    quiet: 'pending',
    reflection: 'pending',
    complete: false,
    reflections: 0,
    salvage: 0,
  };
}
function settle(state, lesson, key, earned, owners) {
  if (lesson[key] !== 'pending') return;
  lesson[key] = earned ? 'earned' : 'missed';
  if (!earned) {
    owners.event(
      state,
      'vesper-lesson-miss',
      key === 'quiet'
        ? 'QUIET GAP · FOLLOW THE BREAK IN THE SOUND WAVE'
        : 'CRYSTAL RETURN · PUT THE CRYSTAL BETWEEN YOU AND THE SOURCE',
    );
    return;
  }
  const scoreAward = key === 'quiet' ? 200 : 250;
  state.score += scoreAward;
  lesson.salvage++;
  state.approachSkillSalvage = (state.approachSkillSalvage ?? 0) + 1;
  owners.earnSalvage(state, 1);
  owners.burst(state, pilot(state), 'cyan', 'impact', { scoreAward, combo: 1 });
  owners.event(
    state,
    'vesper-lesson-clear',
    `${key === 'quiet' ? 'QUIET GAP CLEARED' : 'CRYSTAL RETURN LEARNED'} · +${scoreAward}`,
  );
}

/** Finite optional approach practice. Original crystal triangles and shared125
 * sound contact own every outcome. The lesson never changes wave admission,
 * guardian HP, equipment, route completion or release progression. Missing
 * source data produces no substitute geometry or reward. */
export function stepSkyVesperLesson(state, dt, owners) {
  if (state.startMode === 'boss' || state.boss || state.journey?.phase === 'boss') {
    if (state.vesperLesson) state.vesperLesson.active = false;
    return;
  }
  if (state.status !== 'playing' || !Number.isFinite(dt) || dt <= 0 || dt > 0.1) return;
  if (state.vesperLesson?.sector !== state.sector) state.vesperLesson = create(state);
  const lesson = state.vesperLesson;
  if (!lesson || lesson.complete) return;
  lesson.active = true;
  lesson.age += dt;
  const now = pilot(state),
    source = lesson.props[0],
    reflectors = lesson.props.slice(1);
  for (const prop of lesson.props) prop.flash = Math.max(0, prop.flash - dt * 1.5);
  lesson.baitPoints = reflectors.map((prop) => {
    const t = (now.z - lesson.emitter.z) / (prop.weakpoint.z - lesson.emitter.z);
    return {
      x: lesson.emitter.x + (prop.weakpoint.x - lesson.emitter.x) * t,
      y: lesson.emitter.y + (prop.weakpoint.y - lesson.emitter.y) * t,
      z: now.z,
    };
  });
  if (lesson.age >= 1 && !lesson.emittedRing) {
    lesson.emittedRing = true;
    const p = lesson.emitter;
    lesson.rings.push({
      id: state.nextId++,
      x: p.x,
      y: p.y,
      z: p.z,
      previousZ: p.z,
      radius: 10,
      previousRadius: 10,
      gap: -Math.PI / 2 + 0.42,
      age: 0,
      hit: false,
    });
    source.flash = 1;
  }
  for (const ring of lesson.rings) {
    if (stepSkyVesperRing(ring, dt, lesson.pilot, now)) owners.hit?.(state, 'vesper-lesson-wave');
    if (ring.previousZ < now.z && ring.z >= now.z) {
      const t = (now.z - ring.previousZ) / (ring.z - ring.previousZ);
      const x = lesson.pilot.x + (now.x - lesson.pilot.x) * t - ring.x;
      const y = lesson.pilot.y + (now.y - lesson.pilot.y) * t - ring.y;
      const radius = ring.previousRadius + (ring.radius - ring.previousRadius) * t;
      const angle = Math.atan2(y, x) - ring.gap;
      const inGap =
        Math.abs(Math.atan2(Math.sin(angle), Math.cos(angle))) < SKY_VESPER.gapHalfAngle;
      settle(
        state,
        lesson,
        'quiet',
        !ring.hit && inGap && Math.abs(Math.hypot(x, y) - radius) < 1.6,
        owners,
      );
    }
  }
  lesson.rings = lesson.rings.filter((ring) => ring.z < 20 && ring.age < 5);
  lesson.quietTarget = lesson.rings[0] ? skyVesperQuietTarget(lesson.rings[0], now.z) : null;
  if (lesson.age >= 6 && lesson.age < 9) lesson.phase = 'track';
  if (lesson.age >= 9 && !lesson.emittedPulse) {
    lesson.emittedPulse = true;
    lesson.phase = 'pulse';
    lesson.locked = { ...now };
    lesson.pulses.push({
      id: state.nextId++,
      from: { ...lesson.emitter },
      head: { ...lesson.emitter },
      target: { ...now },
      mode: 'outbound',
      age: 0,
      side: 0,
    });
  }
  for (const pulse of lesson.pulses) {
    const result = stepSkyVesperPulse(
      pulse,
      dt,
      reflectors,
      lesson.emitter,
      source.authoredPose,
      lesson.pilot,
      now,
    );
    if (result?.hit) owners.hit?.(state, 'vesper-lesson-pulse');
    if (result?.reflected) {
      lesson.reflections++;
      owners.event(state, 'vesper-lesson-reflection', 'REAL CRYSTAL CONTACT · FOLLOW THE RETURN');
    }
    if (result?.arrived) {
      source.flash = 1;
      settle(state, lesson, 'reflection', true, owners);
    }
  }
  lesson.pulses = lesson.pulses.filter((pulse) => !pulse.done);
  lesson.pilot = now;
  if (lesson.age >= 17 || lesson.reflection === 'earned') {
    settle(state, lesson, 'quiet', false, owners);
    settle(state, lesson, 'reflection', false, owners);
    lesson.phase = 'complete';
    lesson.complete = true;
    lesson.rings = [];
    lesson.pulses = [];
  }
}

/** Existing HUD telemetry reports practice outcomes, without calling them boss
 * mastery or gating the campaign. Pending/failed attempts never earn salvage. */
export function skyVesperLessonTelemetry(state) {
  const lesson = state.vesperLesson;
  if (!lesson || lesson.sector !== state.sector || state.boss || !lesson.active) return null;
  const earned = [lesson.quiet, lesson.reflection].filter((value) => value === 'earned').length;
  const hint =
    lesson.phase === 'quiet'
      ? 'FLY THROUGH THE BREAK IN THE SOUND WAVE'
      : lesson.phase === 'complete'
        ? `RESONANCE PRACTICE · ${earned}/2 EARNED`
        : 'BAIT THE LOCKED PULSE THROUGH A CRYSTAL';
  return {
    text: hint,
    label: `${hint}. ${earned} of 2 skills practised. ${lesson.salvage} salvage earned. Approach continues independently.`,
    progress: [lesson.quiet, lesson.reflection].filter((value) => value !== 'pending').length / 2,
    complete: lesson.complete,
  };
}
